import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual variant. `elevated` adds halo shadow + lift on hover. */
  variant?: 'default' | 'elevated' | 'glass' | 'gradient' | 'flat' | 'wallet';
}

const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  // Default: highlight interno sutil + shadow Apple-card (depth real)
  default: 'bg-card text-card-foreground rounded-2xl border border-border/50 shadow-octa-card',
  elevated:
    'bg-card text-card-foreground rounded-2xl border border-border/40 shadow-octa-lg hover-lift',
  // Glass premium — backdrop forte com brilho interno
  glass:
    'octa-glass-premium text-card-foreground rounded-2xl border border-white/40 dark:border-white/10',
  // Gradient: hero/wallet vibe + halo
  gradient: 'bg-octa-gradient text-white rounded-2xl border-0 shadow-octa-lg shadow-octa-glow',
  // Flat: lista densa, sem shadow (extratos, drawers)
  flat: 'bg-card text-card-foreground rounded-2xl border border-border/40',
  // Wallet: Apple Wallet style — gradient hero com texturas
  wallet:
    'relative overflow-hidden bg-octa-gradient text-white rounded-3xl border-0 shadow-octa-lg ring-1 ring-black/5',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div ref={ref} className={cn(variantClasses[variant], className)} {...props} />
  ),
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';
